/**
 * Single source of truth mapping route path → roles that may see it.
 * Consumed by:
 *   - RoleGate in App.js (redirects out-of-role users to /dashboard)
 *   - NAV_GROUPS table in AppShell.jsx (hides sidebar links)
 * Backend endpoints still enforce role checks server-side; this is UX gating.
 */
export const ROUTE_ROLES = {
  "/dashboard":  ["owner", "admin", "reception", "therapist", "accountant", "guardian"],
  "/attendance": ["owner", "admin", "reception", "therapist"],
  "/queue":      ["owner", "admin", "reception", "therapist"],
  "/therapists": ["owner", "admin", "reception"],
  "/sessions":   ["owner", "admin", "reception", "therapist"],
  "/students":   ["owner", "admin", "reception", "therapist", "guardian"],
  "/billing":    ["owner", "admin", "reception", "accountant", "guardian"],
  "/messages":   ["owner", "admin", "reception", "therapist", "accountant", "guardian"],
  "/reports":    ["owner", "admin", "accountant"],
  "/audit":      ["owner", "admin"],
  "/settings":   ["owner", "admin"],
  "/inventory":  ["owner", "admin", "reception", "therapist"],
  "/payroll":    ["owner", "admin", "accountant"],
  "/certifications": ["owner", "admin"],
  "/grants":     ["owner", "admin", "accountant"],
  "/appointments": ["owner", "admin", "reception", "therapist", "guardian"],
  // iter-67d — Employees moved to People. Every staff role may
  // navigate; the AppShell filter still runs the `workforce_admin`
  // module gate so users without that permission get the item hidden.
  // Guardians are excluded outright.
  "/workforce/employees": ["owner", "admin", "reception", "therapist", "accountant"],
  "/workforce/schedule":  ["owner", "admin", "reception", "therapist"],
  "/workforce/holidays":  ["owner", "admin"],
  // Phase 4 — Salary Rule Builder. Any role can navigate; access is
  // gated by the `hr_rule_manager` permission at the AppShell filter
  // and via the SalaryRulesPage's own guard.
  "/workforce/salary-rules": ["owner", "admin", "reception", "therapist", "accountant"],
  // iter-67h — My Approvals is a workforce-workflow surface. Parents
  // and guardians never submit or approve internal requests; keep them
  // out of the route allowlist entirely.
  "/my-approvals": ["owner", "admin", "reception", "therapist", "accountant"],
  // iter-57 — QR display / scan pages. Access is additive: any user
  // holding the display_qr or scan_qr additive role. Because the
  // additive roles pair with a primary role (e.g. therapist + scan_qr),
  // we allow every primary role here and rely on the top-bar button
  // visibility (based on the additive role) to keep the entry point
  // consistent. Direct visits by users lacking the additive role are
  // still allowed — the backend gates the token endpoint separately.
  "/qr/display": ["owner", "admin", "reception", "therapist", "accountant", "guardian"],
  "/qr/scan":    ["owner", "admin", "reception", "therapist", "accountant", "guardian"],
  // iter-67h — Publish (workspace-internal stories / articles /
  // advertisements) is not part of the Parent/Guardian portal.
  "/app/publish": ["owner", "admin", "reception", "therapist", "accountant"],
};

export const rolesFor = (path) => ROUTE_ROLES[path] || [];
